<!-- eslint-disable max-len -->
<template lang="pug">
app-main-content-layout(title='')
  .c-requests-page
    //- Header Section
    section.c-requests-page__header
      .c-requests-page__header-content
        h1.c-requests-page__heading Requests
        p.c-requests-page__subtitle Manage organization claim requests

    //- Navigation Tabs Section (Pill Style)
    section.c-requests-page__nav-section
      .c-requests-page__nav-wrapper
        .c-requests-nav
          a.c-requests-nav-link(
            :class="{ 'c-requests-nav-link--active': currentView === 'All' }"
            @click.prevent="handleTabChange('All')"
          ) All
          a.c-requests-nav-link(
            :class="{ 'c-requests-nav-link--active': currentView === 'Pending' }"
            @click.prevent="handleTabChange('Pending')"
          ) Pending
          a.c-requests-nav-link(
            :class="{ 'c-requests-nav-link--active': currentView === 'Approved' }"
            @click.prevent="handleTabChange('Approved')"
          ) Approved
          a.c-requests-nav-link(
            :class="{ 'c-requests-nav-link--active': currentView === 'Declined' }"
            @click.prevent="handleTabChange('Declined')"
          ) Declined

    //- Bulk Actions Section (only show when items selected)
    section.c-requests-page__bulk-section(v-if="selected.length > 0")
      .c-bulk-actions-card
        .c-bulk-actions-card__content
          .c-bulk-actions-card__info
            span.c-bulk-actions-card__count {{ selected.length }} selected
          .c-bulk-actions-card__actions
            el-button.c-bulk-actions-card__button(
              type="success"
              size="large"
              @click="bulkUpdate('Approved')"
            )
              fai(type="fal" icon="check").me-2
              span Approve
            el-button.c-bulk-actions-card__button(
              type="danger"
              size="large"
              @click="bulkUpdate('Declined')"
            )
              fai(type="fal" icon="times").me-2
              span Decline

    //- Filters Section
    section.c-requests-filters-section
      RequestFilters(
        v-model="searchQuery"
        :sort-value="sortBy"
        :sort-options="sortOptions"
        @search="searchFilter"
        @update:sortValue="sortBy = $event"
        @sort="sortTable"
      )

    //- Table Section
    section.c-requests-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--page
            .c-skeleton-cell.c-skeleton-cell--ein
            .c-skeleton-cell.c-skeleton-cell--status
      
      //- Table
      el-table(
        v-else
        :data="items.value"
        @row-click="routeToOrg($event)"
        style="width: 100%"
        @selection-change="onCheckboxChange"
      ).clickable-rows
        el-table-column(width="15")
        el-table-column(type="selection" width="55")
        el-table-column(label="Name" min-width="156")
          template(#default="scope")
            .lh-sm(v-if="scope.row.claimer")
              .font-black {{ scope.row.claimer.firstname }} {{ scope.row.claimer.lastname }}
            .lh-sm(v-else)
              .font-black N/A
        
        el-table-column(label="Email" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.email }}

        el-table-column(label="Page" min-width="230")
          template(#default="scope")
            .lh-sm
              .font-medium.text-decoration-underline {{ scope.row.causeName }}

        el-table-column(label="EIN" min-width="120")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.causeId }}
        
        el-table-column(label="Status" min-width="100")
          template(#default="scope")
            span.status-badge(:class="getBadgeClass(scope.row.claimStatus)") {{ scope.row.claimStatus }}

        el-table-column(width="46")
          template(#default="scope")
            .el-table__expand-icon
              img(src="@/assets/icons/light/chevron-right-light.svg")

        el-table-column(width="15")

      //- Pagination
      .c-requests-pagination
        el-pagination(
          v-model:current-page="page"
          layout="prev, pager, next"
          :total="totalCount"
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
        )
</template>

<script lang="ts">
import {
  defineComponent, ref, onMounted, reactive, watch,
} from 'vue';
import RequestFilters from './RequestFilters.vue';
import shared from '../shared';

export default defineComponent({
  name: 'ManageBenevityRequests',
  components: { RequestFilters },
  setup() {
    const {
      router,
      loading,
      getSyncedBenevityCauses,
      mutateBulkClaimStatus,
    } = shared();
    const items = reactive([]);
    const serverError = ref('');

    const currentView = ref('All');
    const selected = ref([]);
    const searchQuery = ref('');
    const sortOptions = [
      { label: 'EIN', value: 'ein' },
      { label: 'Page', value: 'causeName' },
    ];
    const sortBy = ref('');

    const query = ref(null);
    const claimStatus = ref(null);
    const page = ref(1);
    const pageSize = ref(10);
    const totalCount = ref(0);

    // Track the latest request to prevent race conditions
    let latestRequestId = 0;

    function routeToOrg(itm) {
      router.push(`/manage/benevity-claim/${itm.id}`);
    }

    function sortTable() {
      if (sortBy.value && sortBy.value.value) {
        items.value = items.value.sort((a, b) => {
          const aVal = a[sortBy.value.value] || '';
          const bVal = b[sortBy.value.value] || '';
          return aVal.toString().localeCompare(bVal.toString());
        });
      }
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function getBadgeClass(status) {
      switch (status) {
        case 'Approved':
          return 'status-badge--approved';
        case 'Pending':
          return 'status-badge--pending';
        case 'Declined':
          return 'status-badge--declined';
        default:
          return '';
      }
    }

    async function findSyncedBenevityCauses() {
      // Increment request ID to track this specific request
      const currentRequestId = ++latestRequestId;
      
      try {
        loading.value = true;
        
        const benevityCausesPaginationInput = {
          query: query.value,
          page: page.value,
          pageSize: pageSize.value,
          claimStatus: claimStatus.value,
        };
        const response = await getSyncedBenevityCauses(benevityCausesPaginationInput);
        
        // Only update if this is still the latest request (prevents race conditions)
        if (currentRequestId === latestRequestId) {
          items.value = response.benevityCauses;
          totalCount.value = response.totalCount;
        }
      } catch (err) {
        console.error('Error fetching requests:', err);
      } finally {
        // Only clear loading if this is still the latest request
        if (currentRequestId === latestRequestId) {
          loading.value = false;
        }
      }
    }

    async function bulkUpdate(type) {
      const bulkCauses = [];
      selected.value.forEach((element) => {
        const item = { id: element.id, status: type };
        bulkCauses.push(item);
      });
      await mutateBulkClaimStatus(bulkCauses);
      selected.value = [];
      await findSyncedBenevityCauses();
    }

    async function handleTabChange(filterBy) {
      currentView.value = filterBy;
      page.value = 1;
      
      // Reset search query and update the search input
      query.value = null;
      searchQuery.value = '';

      if (filterBy === 'Pending') {
        claimStatus.value = 'Pending';
      } else if (filterBy === 'Approved') {
        claimStatus.value = 'Approved';
      } else if (filterBy === 'Declined') {
        claimStatus.value = 'Declined';
      } else {
        claimStatus.value = null;
      }

      await findSyncedBenevityCauses();
    }

    const handlePageSizeChange = (val) => {
      console.log(`${val} items per page`);
    };

    async function handleCurrentPageChange(pageNumber) {
      page.value = pageNumber;
      await findSyncedBenevityCauses();
    }

    async function searchFilter(searchValue: string) {
      // Update query value based on search input
      if (searchValue) {
        const trimmedValue = searchValue.trim();
        if (trimmedValue.length > 2) {
          query.value = trimmedValue;
        } else {
          // Don't search if less than 3 characters
          return;
        }
      } else {
        query.value = null;
      }
      
      // Reset to first page when searching
      page.value = 1;
      await findSyncedBenevityCauses();
    }

    onMounted(async () => {
      await findSyncedBenevityCauses();
    });

    return {
      // data (static)
      loading,

      // data (reactive)
      items,
      totalCount,
      serverError,
      selected,
      searchQuery,
      currentView,
      sortOptions,
      sortBy,
      page,
      pageSize,
      // methods
      getBadgeClass,
      handleTabChange,
      onCheckboxChange,
      bulkUpdate,
      sortTable,
      handlePageSizeChange,
      handleCurrentPageChange,
      routeToOrg,
      searchFilter,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue and Activity page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-requests-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__bulk-section {
    margin-top: 0;
    margin-bottom: 0;
  }
}

// Filters Section (no card wrapper)
.c-requests-filters-section {
  margin-top: 0;
  margin-bottom: 0;
}

// Navigation Tabs (Pill Style) - Match Activity page
.c-requests-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: nowrap;
}

.c-requests-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  padding: 6px 32px;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $gray-600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: $gray-900;
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: white;
    color: $gray-900;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }
}

// Bulk Actions Card
.c-bulk-actions-card {
  background: white;
  border-radius: 12px;
  border: 1px solid $gray-200;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding: 1rem 1.5rem;

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__count {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-900;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__button {
    :deep(.el-button) {
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.875rem;
      padding: 8px 16px;
      transition: all 0.2s ease;
    }
  }
}

// Table Section - Match Activity page exactly
.c-requests-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  // Enable horizontal scrolling
  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  // Table styling - Match Activity page
  :deep(.el-table) {
    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination - Outlined style
.c-requests-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem;
  border-top: 1px solid $gray-100;

  :deep(.el-pagination) {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .btn-prev,
    .btn-next {
      background: transparent;
      border: 1px solid $gray-300;
      border-radius: 8px;
      padding: 8px 12px;
      font-weight: 500;
      color: $gray-700;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        border-color: $color-purple-text;
        color: $color-purple-text;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .el-pager {
      display: flex;
      gap: 0.25rem;

      li {
        min-width: 36px;
        height: 36px;
        line-height: 36px;
        border-radius: 8px;
        font-weight: 500;
        color: $gray-700;
        background: transparent;
        border: 1px solid $gray-300;
        transition: all 0.2s ease;

        &:hover {
          border-color: $color-purple-text;
          color: $color-purple-text;
        }

        &.is-active {
          background: transparent;
          border-color: $color-purple-text;
          color: $color-purple-text;
          font-weight: 600;
        }
      }
    }
  }
}

// Skeleton Loader
.c-skeleton-loader {
  padding: 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid $gray-100;
  gap: 1rem;

  &:first-child {
    border-top: 1px solid $gray-100;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(
    90deg,
    $gray-100 0%,
    $gray-50 50%,
    $gray-100 100%
  );
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  border-radius: 4px;

  &--checkbox {
    width: 55px;
    flex-shrink: 0;
  }

  &--name {
    width: 156px;
    flex-shrink: 0;
  }

  &--email {
    width: 200px;
    flex-shrink: 0;
  }

  &--page {
    width: 230px;
    flex-shrink: 0;
  }

  &--ein {
    width: 120px;
    flex-shrink: 0;
  }

  &--status {
    width: 100px;
    flex-shrink: 0;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Status badges - Match Activity page badge style exactly
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--approved {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--pending {
    background-color: #FFE8D6;
    color: #D97706;
    border-color: #FB923C;
  }

  &--declined {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Checkbox styling
:deep(.el-checkbox__label) {
  display: none;
}

// Responsive Design
@media (max-width: 768px) {
  .c-requests-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }

  .c-requests-nav {
    width: 100%;
    overflow-x: auto;
  }

  .c-requests-nav-link {
    font-size: 0.875rem;
    padding: 4px 24px;
  }

  .c-bulk-actions-card {
    &__content {
      flex-direction: column;
      align-items: flex-start;
    }

    &__actions {
      width: 100%;
      
      button {
        flex: 1;
      }
    }
  }
}

@media (max-width: 480px) {
  .c-requests-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
